import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../utils/translations';
import { applicationsAPI } from '../services/api';
import './AgentApplyPage.css';

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  address: '',
  mobile: '',
};

const AgentApplyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialForm);
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    setCvFile(event.target.files?.[0] || null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (!cvFile) {
      setError(t('agentApply.cvRequired'));
      return;
    }

    const payload = new FormData();
    payload.append('firstName', formData.firstName.trim());
    payload.append('lastName', formData.lastName.trim());
    payload.append('email', formData.email.trim());
    payload.append('address', formData.address.trim());
    payload.append('mobile', formData.mobile.trim());
    payload.append('cv', cvFile);

    try {
      setSubmitting(true);
      await applicationsAPI.submit(payload);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || t('agentApply.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="agent-apply-page">
        <div className="container">
          <div className="agent-apply-card agent-apply-success">
            <h1>{t('agentApply.successTitle')}</h1>
            <p>{t('agentApply.successMessage')}</p>
            <button
              type="button"
              className="agent-apply-btn"
              onClick={() => navigate('/agents')}
            >
              {t('agentApply.backToAgents')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-apply-page">
      <div className="container">
        <div className="agent-apply-header">
          <h1>{t('agentApply.title')}</h1>
          <p>{t('agentApply.subtitle')}</p>
        </div>

        <form className="agent-apply-card agent-apply-form" onSubmit={handleSubmit}>
          <div className="agent-apply-grid">
            <div className="agent-apply-field">
              <label htmlFor="firstName">{t('agentApply.firstName')} *</label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="agent-apply-field">
              <label htmlFor="lastName">{t('agentApply.lastName')} *</label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="agent-apply-field">
              <label htmlFor="email">{t('agentApply.email')} *</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="agent-apply-field">
              <label htmlFor="mobile">{t('agentApply.mobile')} *</label>
              <input
                id="mobile"
                name="mobile"
                type="tel"
                value={formData.mobile}
                onChange={handleChange}
                required
              />
            </div>

            <div className="agent-apply-field agent-apply-field-full">
              <label htmlFor="address">{t('agentApply.address')} *</label>
              <input
                id="address"
                name="address"
                type="text"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>

            <div className="agent-apply-field agent-apply-field-full">
              <label htmlFor="cv">{t('agentApply.cv')} *</label>
              <input
                id="cv"
                name="cv"
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileChange}
                required
              />
              <span className="agent-apply-hint">{t('agentApply.cvHint')}</span>
            </div>
          </div>

          {error && <p className="agent-apply-error">{error}</p>}

          <div className="agent-apply-actions">
            <button
              type="button"
              className="agent-apply-btn secondary"
              onClick={() => navigate('/agents')}
              disabled={submitting}
            >
              {t('agentApply.backToAgents')}
            </button>
            <button type="submit" className="agent-apply-btn" disabled={submitting}>
              {submitting ? t('agentApply.submitting') : t('agentApply.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentApplyPage;
